/**
 * What the Netly Pay overview shows, worked out from the API payload.
 *
 * Kept out of the component so the figures can be tested without rendering
 * anything. The spec is `vOverview()` in docs/design/netly-pay/prototype.html
 * and DESIGN-BRIEF.md §B.
 */
import {
  addDays,
  fmtShort,
  money,
  moneyShort,
  todayISO,
  weekStart,
  type Session,
} from '../../internals/managers/NetlyPayManager';
import type {OverviewPayload} from '../../internals/repository/NetlyPayRepository';

export interface StatCard {
  label: string;
  value: string;
  sub: string;
  /** Overdue reads in the danger colour, waiting in the brand colour. */
  tone: 'default' | 'danger' | 'accent';
  /** Expected carries a progress bar; the others do not. */
  barPct: number | null;
  to: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The four cards, each linking to the screen that explains it.
 *
 * `collected / expected` is deliberately clamped for the bar but not for the
 * label: a month where more came in than was billed (someone paying next
 * month early) should read 104%, not 100%, but the bar must not overflow.
 */
export function overviewStats(payload: OverviewPayload, currency = 'Rs.'): StatCard[] {
  const collected = payload?.collectedMinor ?? 0;
  const expected = payload?.expectedMinor ?? 0;
  const overdue = payload?.overdue ?? {amountMinor: 0, requests: 0, members: 0};
  const waiting = payload?.waiting ?? {count: 0, amountMinor: 0};
  const pct = expected ? Math.round((collected / expected) * 100) : 0;
  const payments = (payload?.activity ?? []).filter((a) => a.kind === 'paid').length;

  return [
    {
      label: 'COLLECTED THIS MONTH',
      value: money(collected, currency),
      sub: payments ? `${plural(payments, 'payment', 'payments')} this month` : 'No payments yet',
      tone: 'default',
      barPct: null,
      to: '/netly-pay/records',
    },
    {
      label: 'EXPECTED',
      value: money(expected, currency),
      sub: expected ? `${pct}% collected so far` : 'Nothing billed yet',
      tone: 'default',
      barPct: Math.min(100, pct),
      to: '/payment-requests',
    },
    {
      label: 'OVERDUE',
      value: money(overdue.amountMinor, currency),
      sub: `${plural(overdue.requests, 'request', 'requests')} · ${plural(
        overdue.members,
        'member',
        'members',
      )}`,
      tone: overdue.amountMinor > 0 ? 'danger' : 'default',
      barPct: null,
      to: '/payment-requests?tab=overdue',
    },
    {
      label: 'WAITING FOR APPROVAL',
      value: String(waiting.count ?? 0),
      sub: `${money(waiting.amountMinor, currency)} to review`,
      tone: waiting.count ? 'accent' : 'default',
      barPct: null,
      to: '/approvals',
    },
  ];
}

export interface WeekBar {
  week: string;
  label: string;
  value: string;
  /** Percent of the tallest bar, so the chart always fills its box. */
  heightPct: number;
  isCurrent: boolean;
}

/**
 * Eight weeks of collections, oldest first, ending with the current week.
 *
 * The current week is striped and labelled "This week" because it is
 * incomplete — a bar half the height of last week's means three days have
 * passed, not that collections halved.
 */
export function weekBars(
  weeks: Record<string, number> | undefined,
  today: string = todayISO(),
  currency = 'Rs.',
): WeekBar[] {
  const current = weekStart(today);
  const start = addDays(current, -7 * 7);
  const buckets: WeekBar[] = [];
  let max = 1;

  for (let i = 0; i < 8; i += 1) {
    const week = addDays(start, i * 7);
    const value = Number((weeks ?? {})[week] ?? 0);
    max = Math.max(max, value);
    buckets.push({
      week,
      label: week === current ? 'This week' : fmtShort(week),
      value: value ? moneyShort(value, currency) : '—',
      heightPct: value,
      isCurrent: week === current,
    });
  }

  return buckets.map((b) => ({...b, heightPct: Math.round((b.heightPct / max) * 100)}));
}

export type SessionState = 'Upcoming' | 'In progress' | 'Finished';

/** Where a session sits against the clock. `now` is `HH:MM`. */
export function sessionState(session: {start: string; end: string}, now: string): SessionState {
  if (now < session.start) return 'Upcoming';
  if (now < session.end) return 'In progress';
  return 'Finished';
}

export interface SessionRow {
  id: string;
  time: string;
  name: string;
  where: string;
  expected: string;
  state: SessionState;
}

export function todaySessions(
  payload: OverviewPayload,
  today: string = todayISO(),
  now = '00:00',
): SessionRow[] {
  const batches = payload?.batches ?? [];
  const programs = payload?.programs ?? [];
  const members = payload?.members ?? [];

  return (payload?.sessions ?? [])
    .filter((s: Session) => s.date === today)
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((s) => {
      const batch = batches.find((b) => b.id === s.batchId);
      const program = programs.find((p) => p.id === batch?.programId);
      // Expected = members actually training: a frozen or pending member is
      // on the roster but is not coming.
      const expected = members.filter((m) => m.batchId === s.batchId && !m.state).length;
      return {
        id: s.id,
        time: `${s.start} – ${s.end}`,
        name: [program?.name, batch?.name].filter(Boolean).join(' · ') || 'Session',
        where: [batch?.venue, batch?.coach].filter(Boolean).join(' · '),
        expected: `${expected} expected`,
        state: sessionState(s, now),
      };
    });
}

/** The activity feed's glyph and tone per kind. */
export const ACTIVITY_GLYPH: Record<string, {glyph: string; classes: string}> = {
  proof: {glyph: '↑', classes: 'bg-brand-soft text-brand'},
  paid: {glyph: '✓', classes: 'bg-pos-soft text-pos'},
  remind: {glyph: '!', classes: 'bg-warn-soft text-warn'},
  join: {glyph: '+', classes: 'bg-info-soft text-info'},
  reject: {glyph: '×', classes: 'bg-neg-soft text-neg'},
  request: {glyph: '→', classes: 'bg-surface3 text-text2'},
};

export function activityGlyph(kind: string) {
  return ACTIVITY_GLYPH[kind] ?? ACTIVITY_GLYPH.request;
}
